import {
	ICandidateGroupedExperience,
	Stint,
} from "mapx-components/Cards/CandidateCard/CandidateExperience/types";
import type {SICandidatePosition} from "api/candidateApi/types";

export function CandidateCareerHistory(
	positions: SICandidatePosition[],
): ICandidateGroupedExperience[] {
	if (!positions?.length) return [];
	const NOW_YEAR = new Date().getFullYear();
	// 1) bucket by company
	const byCompany = new Map<number, SICandidatePosition[]>();

	positions.forEach((exp) => {
		const cid = exp.company?.id;
		if (!cid) return;

		const companyExps = byCompany.get(cid) || [];
		companyExps.push(exp);
		byCompany.set(cid, companyExps);
	});

	const allGroups: ICandidateGroupedExperience[] = [];

	byCompany.forEach((exps) => {
		// 2) sort ascending by start_date
		exps.sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

		let current: Stint | null = null;

		exps.forEach((exp) => {
			const {company, ...rest} = exp;
			const events = company.events || [];
			const sY = new Date(exp.start_date).getFullYear();
			const eY = exp.end_date ? new Date(exp.end_date).getFullYear() : NOW_YEAR;

			if (!current) {
				// first stint
				current = {company, startYear: sY, endYear: eY, details: [{...rest, events}]};
			} else if (sY <= current.endYear) {
				// contiguous or overlapping by year → merge
				current.endYear = Math.max(current.endYear, eY);
				current.details.push({...rest, events});
			} else if (current) {
				// real year gap → close out previous stint
				allGroups.push({
					company: current.company,
					details: current.details.sort(
						(a, b) =>
							new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
					),
				});

				// start new stint
				current = {company, startYear: sY, endYear: eY, details: [{...rest, events}]};
			}
		});

		// push last company stint

		if (current !== null) {
			allGroups.push({
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				company: current.company,
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				details: current.details.sort(
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					(a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
				),
			});
		}
	});

	// 3) sort *all* stints reverse-chronologically by their most recent role
	allGroups.sort((a, b) => {
		const aCur = a.details[0].is_current ? 1 : 0;
		const bCur = b.details[0].is_current ? 1 : 0;

		if (aCur !== bCur) {
			// current-first
			return bCur - aCur;
		}

		// otherwise, by recency of first detail
		const aDate = new Date(a.details[0].start_date).getTime();
		const bDate = new Date(b.details[0].start_date).getTime();

		return bDate - aDate;
	});

	return allGroups.reduce((acc: ICandidateGroupedExperience[], curr) => {
		const last = acc[acc.length - 1];
		if (last && last.company.id === curr.company.id) {
			// the same company as the previous group → append its details
			last.details = last.details.concat(curr.details);
		} else {
			// new company (or first item) → push a shallow copy
			acc.push({
				company: curr.company,
				details: [...curr.details],
			});
		}

		return acc;
	}, []);
}
